/**
 * Ordnerwahl beim Ablegen von E-Mail-Anhängen (Kundenfeedback 18.10.2026,
 * XX): nur die Ordner, die die Akte tatsächlich hat (automatisch angelegt,
 * bedingt, selbst angelegt, Beteiligten-Ordner) – plus getrennt die Vorlagen,
 * die erst beim Ablegen angelegt werden. Quelle ist der Endpunkt
 * GET /protected/cases/{id}/doc-folders/targets (Paket Dokumente).
 *
 * Werte im Auswahlfeld:
 *   - Ordner der Akte: die Kategorie des Dokuments ("sonstiges", "folder:12")
 *   - vordefinierte Vorlage: "template-class:<classId>"
 *   - eigene Vorlage der Organisation: "template:<templateId>"
 */
import { gt } from '@/i18n/gt'

export interface FolderTarget {
  value: string
  label: string
  /** Kategorie beim Ablegen; bei eigener Vorlage erst nach dem Anlegen bekannt. */
  category: string
  kind: string
  classId?: string
  templateId?: number
}

export interface FolderTargets {
  folders: FolderTarget[]
  templates: FolderTarget[]
  defaultCategory: string
}

export interface FolderGroup {
  label: string
  options: FolderTarget[]
}

const TEMPLATE_PREFIX = 'template:'
const TEMPLATE_CLASS_PREFIX = 'template-class:'

type RawTarget = Record<string, unknown>

function asRecord (v: unknown): RawTarget | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? v as RawTarget : null
}

function str (v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

function toFolder (raw: unknown): FolderTarget | null {
  const r = asRecord(raw)
  const category = str(r?.category)
  if (!r || !category) return null
  return { value: category, category, label: str(r.path) || str(r.label) || category, kind: str(r.kind) }
}

function toTemplate (raw: unknown): FolderTarget | null {
  const r = asRecord(raw)
  if (!r) return null
  const label = str(r.path) || str(r.label)
  const classId = str(r.classId)
  const templateId = Number(r.templateId)
  if (classId && str(r.category)) {
    return { value: TEMPLATE_CLASS_PREFIX + classId, category: str(r.category), label, kind: str(r.kind), classId }
  }
  if (Number.isInteger(templateId) && templateId > 0) {
    return { value: TEMPLATE_PREFIX + templateId, category: '', label, kind: str(r.kind), templateId }
  }
  return null
}

function list (v: unknown, map: (raw: unknown) => FolderTarget | null): FolderTarget[] {
  return Array.isArray(v) ? v.map(map).filter((t): t is FolderTarget => t !== null && t.label !== '') : []
}

/** Antwort des Endpunkts lesen – mit oder ohne data-Umschlag, robust gegen Lücken. */
export function normalizeFolderTargets (body: unknown): FolderTargets {
  const outer = asRecord(body)
  const data = asRecord(outer?.data) || outer
  return {
    folders: list(data?.folders, toFolder),
    templates: list(data?.templates, toTemplate),
    defaultCategory: str(data?.defaultCategory),
  }
}

export function folderGroups (targets: FolderTargets): FolderGroup[] {
  const groups: FolderGroup[] = [{ label: gt('Ordner der Akte'), options: targets.folders }]
  if (targets.templates.length > 0) {
    groups.push({ label: gt('Vorlage – wird beim Ablegen angelegt'), options: targets.templates })
  }
  return groups
}

/** Vorschlag des Servers, sofern die Akte den Ordner hat – sonst der erste Ordner. */
export function pickDefaultFolder (targets: FolderTargets): string {
  if (targets.folders.some(f => f.value === targets.defaultCategory)) {
    return targets.defaultCategory
  }
  return targets.folders[0]?.value || ''
}

export function isTemplateValue (value: string): boolean {
  return value.startsWith(TEMPLATE_PREFIX) || value.startsWith(TEMPLATE_CLASS_PREFIX)
}

export function findTarget (targets: FolderTargets, value: string): FolderTarget | undefined {
  return [...targets.folders, ...targets.templates].find(t => t.value === value)
}

/** Beschriftung eines gewählten Werts (für Rückmeldungen). */
export function targetLabel (targets: FolderTargets | null, value: string): string {
  return (targets && findTarget(targets, value)?.label) || value
}
